'use client';

import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reason: string, details: string) => void;
}

interface ReportFormData {
  reason: string;
  details: string;
}

const REPORT_REASONS = [
  { id: 'nudity', label: 'Nudity or sexual content', icon: '🚫' },
  { id: 'harassment', label: 'Harassment or bullying', icon: '😡' },
  { id: 'hate-speech', label: 'Hate speech or discrimination', icon: '⚠️' },
  { id: 'spam', label: 'Spam or bot behavior', icon: '🤖' },
  { id: 'minor', label: 'Suspected minor', icon: '🔞' },
  { id: 'other', label: 'Other violation', icon: '📝' },
];

export default function ReportModal({ isOpen, onClose, onSubmit }: ReportModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ReportFormData>({ defaultValues: { reason: '', details: '' } });

  const selectedReason = watch('reason');

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        reset();
        setSubmitted(false);
        setIsSubmitting(false);
      }, 300);
    }
  }, [isOpen, reset]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  const onFormSubmit = async (data: ReportFormData) => {
    setIsSubmitting(true);
    // Backend integration point: POST /api/report
    await new Promise((r) => setTimeout(r, 1200));
    setIsSubmitting(false);
    setSubmitted(true);
    onSubmit(data.reason, data.details);
    setTimeout(onClose, 2000);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-modal-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-card rounded-2xl shadow-modal w-full max-w-md fade-in border border-border">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <div className="w-9 h-9 rounded-xl bg-destructive/10 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
            </svg>
          </div>
          <div>
            <h2 id="report-modal-title" className="text-base font-semibold text-foreground">Report User</h2>
            <p className="text-xs text-muted-foreground">Help us keep ChatConnect safe for everyone</p>
          </div>
          <button
            onClick={onClose}
            className="ml-auto w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Close report modal"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {submitted ? (
          <div className="px-5 py-8 text-center fade-in">
            <div className="w-14 h-14 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-foreground mb-1">Report submitted</h3>
            <p className="text-sm text-muted-foreground">Our moderation team will review this within 24 hours. Thank you for keeping the community safe.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onFormSubmit)} className="px-5 py-4 space-y-4">
            {/* Reason selection */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                What's the issue?
                <span className="text-destructive ml-1">*</span>
              </label>
              <div className="space-y-1.5">
                {REPORT_REASONS.map((r) => (
                  <label
                    key={`reason-${r.id}`}
                    className={`flex items-center gap-3 p-2.5 rounded-xl cursor-pointer border transition-all ${
                      selectedReason === r.id
                        ? 'border-primary bg-primary/5' :'border-transparent hover:bg-muted'
                    }`}
                  >
                    <input
                      type="radio"
                      value={r.id}
                      {...register('reason', { required: 'Please select a reason' })}
                      className="sr-only"
                    />
                    <span className="text-base">{r.icon}</span>
                    <span className="text-sm text-foreground">{r.label}</span>
                    {selectedReason === r.id && (
                      <div className="ml-auto w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                        <div className="w-1.5 h-1.5 rounded-full bg-white" />
                      </div>
                    )}
                  </label>
                ))}
              </div>
              {errors.reason && (
                <p className="text-destructive text-xs mt-1.5">{errors.reason.message}</p>
              )}
            </div>

            {/* Additional details */}
            <div>
              <label htmlFor="report-details" className="block text-sm font-medium text-foreground mb-1">
                Additional details
              </label>
              <p className="text-xs text-muted-foreground mb-1.5">
                Optional — describe what happened to help our review team
              </p>
              <textarea
                id="report-details"
                {...register('details', { maxLength: { value: 500, message: 'Maximum 500 characters' } })}
                rows={3}
                placeholder="Describe the incident..."
                className="w-full rounded-xl border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 resize-none transition-all"
              />
              {errors.details && (
                <p className="text-destructive text-xs mt-1">{errors.details.message}</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2.5 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-2.5 rounded-xl border border-border text-sm font-medium text-foreground hover:bg-muted transition-all active:scale-95"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 py-2.5 rounded-xl bg-destructive text-white text-sm font-semibold hover:bg-destructive/90 transition-all active:scale-95 disabled:opacity-70 flex items-center justify-center gap-2"
                style={{ minWidth: 0 }}
              >
                {isSubmitting ? (
                  <>
                    <svg className="w-4 h-4 queue-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Submitting...
                  </>
                ) : (
                  'Submit Report'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}